import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import AudioRecorder from './AudioRecorder';

interface AudioModalProps {
  photoId: string;
  onClose: () => void;
}

interface AudioMessage {
  messageId: string;
  authorId: string;
  authorName: string;
  audioUrl: string;
  durationSeconds: number;
  createdAt: string;
}

export default function AudioModal({ photoId, onClose }: AudioModalProps) {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuthStore();
  const [showRecorder, setShowRecorder] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['messages', photoId],
    queryFn: () => api.listMessages(photoId),
  });

  const messages: AudioMessage[] = messagesData?.messages || [];

  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => api.deleteMessage(photoId, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', photoId] });
    },
  });

  const playAudio = (messageId: string, url: string) => {
    if (playingId === messageId) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play();
      setPlayingId(messageId);
      audio.onended = () => setPlayingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">🎤 Audio Messages</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Recording Section */}
          {showRecorder ? (
            <AudioRecorder
              photoId={photoId}
              onRecordingComplete={() => setShowRecorder(false)}
              onCancel={() => setShowRecorder(false)}
            />
          ) : (
            <button
              onClick={() => setShowRecorder(true)}
              className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
              </svg>
              Record New Message
            </button>
          )}

          {/* Messages List */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">
              Messages ({messages.length})
            </h3>

            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No messages yet. Be the first!
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.messageId}
                  className="bg-gray-50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {message.authorName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {message.durationSeconds}s ·{' '}
                      {new Date(message.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => playAudio(message.messageId, message.audioUrl)}
                      className="p-2 bg-primary-100 hover:bg-primary-200 rounded-full transition-colors"
                    >
                      {playingId === message.messageId ? '⏸️' : '▶️'}
                    </button>

                    {(user?.userId === message.authorId || isAdmin) && (
                      <button
                        onClick={() => deleteMutation.mutate(message.messageId)}
                        disabled={deleteMutation.isPending}
                        className="p-2 bg-red-100 hover:bg-red-200 rounded-full transition-colors disabled:opacity-50"
                        title="Delete audio message"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

